import type { Attachment } from '@recruitee/attachments-types';
import type { Department } from '@recruitee/departments-types';
import type { LocationBrief } from '@recruitee/locations-types';
import type { OfferBrief, SalaryData } from '@recruitee/offers-types';

import { RequisitionFormFieldKey } from './requisitions-settings.types';

// copy due to circular dependency
export enum SortDirection {
  Asc = 'asc',
  Desc = 'desc',
}

export type Requisition = {
  id: number;
  title: string;
  departmentId: number | null;
  departmentName: string | null;
  approvalFlowId: number | null;
  guid: string;
  status: RequisitionStatus;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  rejectionReason?: string;
  rejectedById?: number;
  approvalRequest?: ApprovalRequestData;
  followed: boolean;
  followerIds: number[];
  assignedOfferIds: number[];
  stateName: string | null;
  location: string | null;
  locationIds: number[];
  offers: OfferBrief[];
  generalContextHtml: string | null;
  form: {
    fields: RequisitionDetailsFormFields[];
  };
  // FORM FIELDS
  category: string | null;
  city: string | null;
  countryCode: string | null;
  education: string | null;
  employmentType: string | null;
  experience: string | null;
  generalContextJson: object | null;
  hiringBudget: number | null;
  hiringBudgetCurrency: string | null;
  hiringManagerId: number;
  hybrid: boolean;
  maxHours: number | null;
  minHours: number | null;
  onSite: boolean;
  openings: RequisitionOpening[];
  recruiterId: number | null;
  remote: boolean;
  salary: SalaryData;
  stateCode: number | null;
  street: string | null;
  // COUNTERS
  offersCount: number;
  hiresCount: number;
  attachmentsCount: number;
  notesCount: number;
  openingsCount: number;
  openingsWithDeletedCandidateCount: number;
  sharedOpeningsCount: number;
  // NGRX REFERENCES
  department?: Department | null;
  locations?: LocationBrief[];
};

export type JobRequisition = Pick<
  Requisition,
  | 'id'
  | 'title'
  | 'departmentId'
  | 'department'
  | 'status'
  | 'guid'
  | 'createdAt'
  | 'updatedAt'
  | 'archivedAt'
  | 'location'
  | 'offers'
  | 'offersCount'
  | 'openings'
  | 'openingsCount'
  | 'sharedOpeningsCount'
  | 'remote'
  | 'countryCode'
  | 'stateCode'
  | 'city'
  | 'locationIds'
  | 'locations'
> & {
  dataMismatch: boolean;
  hasFilledOpeningsInJob: boolean;
};

export enum RequisitionStatus {
  Draft = 'draft',
  Pending = 'pending',
  Rejected = 'rejected',
  Approved = 'approved',
  Filled = 'filled',
}

export enum RequisitionsScope {
  Active = 'active',
  Archived = 'archived',
  NeedMyApproval = 'need_my_approval',
  RequestedByMe = 'requested_by_me',
  ActiveApproved = 'active_approved',
}

export enum RequisitionsGroupKind {
  Department = 'department',
  Status = 'status',
  Location = 'location',
  Country = 'country',
  State = 'state',
  Recruiter = 'recruiter',
  HiringManager = 'hiring_manager',
  WorkModel = 'work_model',
}

export type RequisitionsConfig = {
  enabled: boolean;
  departmentsRequiredForApprovalFlows: boolean;
  requisitionsRequiredForOffers: boolean;
  autoCreateJobAfterApproval: boolean;
};

export type RequisitionsForm = {
  fields: RequisitionFormFieldKey[];
  pristine: boolean;
};

export type RequisitionFlow = {
  id?: number;
  title: string;
  stepsCount?: number;
  default?: boolean;
  departmentIds?: number[];
  steps: RequisitionFlowStep[];
  position?: number;
};

export type RequisitionFlowStep = {
  id?: number;
  position: number;
  kind: RequisitionFlowStepKind;
  allowsEdit: boolean;
  approverIds: number[];
};

export enum RequisitionFlowStepKind {
  All = 'all',
  Any = 'any',
}

export type RequisitionAssignment = {
  id: number;
  adminId: number;
  followed: boolean;
  isHiringManager: boolean;
  isRecruiter: boolean;
  isApprover: boolean;
};

export type RequisitionOpening = {
  id: number;
  reason: RequisitionOpeningReason | null;
  explanation: string | null;
  anticipatedStartDate: string | null;
  status: RequisitionOpeningStatus;
  filledAt: string;
  placement?: RequisitionPlacement;
};

export enum RequisitionOpeningReason {
  Replacement = 'replacement',
  NewPosition = 'new_position',
}

export enum RequisitionOpeningStatus {
  Open = 'open',
  Filled = 'filled',
}

export type RequisitionDetailsFormFields = Exclude<
  RequisitionFormFieldKey,
  'requisition_title' | 'number_of_openings'
>;

export type ApprovalRequestData = {
  id: number;
  attachments: Attachment[];
  bodyHtml: string;
  bodyJson: object;
  requesterId: number;
  viewedByRequester: number;
  createdAt: string;
};

export type RequisitionAvailableFlow = {
  assigned: boolean;
  custom: boolean;
  id: number;
  title: string;
};

export enum DecisionStatus {
  Requested = 'requested',
  Approved = 'approved',
  Rejected = 'rejected',
  Canceled = 'canceled',
  NotRequested = 'not_requested',
}

export type ApprovalStepWithDecisions = RequisitionFlowStep & {
  approverDecisions?: ApproverDecision[];
  status?: 'pending' | 'current';
};

export type ApproverDecision = {
  id: number;
  approverId: number;
  status: DecisionStatus;
  submittedAt: string | null;
  remindedAt: string | null;
  reason: string | null;
};

export type RequisitionAssignedFlow = RequisitionAvailableFlow & {
  steps: ApprovalStepWithDecisions[];
};

// copy due to circular dependency
export type CandidateRequisition = {
  id: number;
  name: string;
  photoThumbUrl: string;
  deletedAt: string;
  initials: string;
};

export type RequisitionPlacement = {
  id: number;
  disqualifyReason?: string;
  offer: OfferBrief | null;
  candidate: CandidateRequisition;
};

export enum RequisitionColumnId {
  Title = 'title',
  Guid = 'guid',
  Status = 'status',
  Department = 'department_name',
  DateCreated = 'created_at',
  LocationName = 'location_name',
  LocationAddress = 'location_address',
  WorkModel = 'work_model',
  State = 'state_name',
  Jobs = 'jobs',
  Followers = 'followers',
  Hires = 'hires_count',
  Recruiter = 'recruiter_name',
  HiringManager = 'hiring_manager_name',
  NumberOfOpenings = 'openings_count',
  ReasonForOpening = 'reason_for_opening',
  Requester = 'requester_name',
  Manage = 'manage',
}

export enum RequisitionsListParam {
  Scope = 'scope',
  Query = 'q',
  GroupBy = 'group_by',
  SortBy = 'sort_by',
  SortOrder = 'sort_order',
}

export type RequisitionsParams = {
  [RequisitionsListParam.Scope]: RequisitionsScope;
  [RequisitionsListParam.Query]?: string;
  [RequisitionsListParam.GroupBy]?: RequisitionsGroupKind | null;
  [RequisitionsListParam.SortBy]?: RequisitionColumnId;
  [RequisitionsListParam.SortOrder]?: SortDirection;
};

export type PlacementOpening = RequisitionOpening & { requisition: JobRequisition };
